import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

const INDICATOR_HIDE_DELAY_MS = 1500;
const PAGE_CENTER_BAND_ROOT_MARGIN = '-35% 0px -35% 0px';
const PAGE_INTERSECTION_THRESHOLDS = [0, 0.01, 0.1, 0.25, 0.5, 0.75, 1];

function getPageNumberFromElement(element: Element): number | null {
    const pageId = element.id;
    const pageNum = Number.parseInt(pageId.replace('report-page-', ''), 10);

    return Number.isNaN(pageNum) ? null : pageNum;
}

function getClosestPageNumber(container: HTMLDivElement, pageElements: Element[]): number | null {
    const containerCenter = container.scrollTop + container.clientHeight / 2;
    let closestPage: number | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;

    pageElements.forEach((pageElement) => {
        if (!(pageElement instanceof HTMLElement)) return;

        const pageNumber = getPageNumberFromElement(pageElement);

        if (pageNumber === null) return;

        const pageCenter = pageElement.offsetTop + pageElement.offsetHeight / 2;
        const distance = Math.abs(pageCenter - containerCenter);

        if (distance < closestDistance) {
            closestDistance = distance;
            closestPage = pageNumber;
        }
    });

    return closestPage;
}

export function usePageScrollTracker(
    containerRef: RefObject<HTMLDivElement | null>,
    totalPages: number,
) {
    const [scrolledPage, setScrolledPage] = useState(1);
    const [isIndicatorVisible, setIsIndicatorVisible] = useState(false);
    const currentPageRef = useRef(1);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pageRatiosRef = useRef(new Map<number, number>());

    const showIndicator = useCallback(() => {
        setIsIndicatorVisible(true);

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            setIsIndicatorVisible(false);
        }, INDICATOR_HIDE_DELAY_MS);
    }, []);

    const updateCurrentPage = useCallback(
        (pageNumber: number | null) => {
            if (pageNumber === null || pageNumber === currentPageRef.current) {
                return;
            }

            currentPageRef.current = pageNumber;
            setScrolledPage(pageNumber);
            showIndicator();
        },
        [showIndicator],
    );

    useEffect(() => {
        const container = containerRef.current;

        if (!container || typeof IntersectionObserver === 'undefined') {
            return;
        }

        let pageElements: Element[] = [];
        const pageRatios = pageRatiosRef.current;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const pageNumber = getPageNumberFromElement(entry.target);

                    if (pageNumber === null) return;

                    pageRatios.set(pageNumber, entry.isIntersecting ? entry.intersectionRatio : 0);
                });

                let nextPage: number | null = null;
                let bestRatio = 0;

                pageRatios.forEach((ratio, pageNumber) => {
                    if (ratio > bestRatio) {
                        bestRatio = ratio;
                        nextPage = pageNumber;
                    }
                });

                if (nextPage === null) {
                    nextPage = getClosestPageNumber(container, pageElements);
                }

                updateCurrentPage(nextPage);
            },
            {
                root: container,
                rootMargin: PAGE_CENTER_BAND_ROOT_MARGIN,
                threshold: PAGE_INTERSECTION_THRESHOLDS,
            },
        );

        const connectObserver = () => {
            observer.disconnect();
            pageRatios.clear();

            pageElements = Array.from(container.querySelectorAll('[id^="report-page-"]'));

            const initialPage = getClosestPageNumber(container, pageElements);

            if (initialPage !== null) {
                currentPageRef.current = initialPage;
                setScrolledPage(initialPage);
            }

            pageElements.forEach((pageElement) => {
                observer.observe(pageElement);
            });
        };

        connectObserver();

        const mutationObserver = new MutationObserver(connectObserver);
        mutationObserver.observe(container, {
            childList: true,
            subtree: true,
        });

        return () => {
            observer.disconnect();
            mutationObserver.disconnect();
            pageRatios.clear();
        };
    }, [containerRef, totalPages, updateCurrentPage]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return { scrolledPage, isIndicatorVisible };
}
